import React, { useState } from 'react';
import { getCsrfToken as csrfToken } from '../../../lib/csrf.js';
import { Link, router } from '@inertiajs/react';

const inputClass =
  'block w-full rounded-lg border border-gray-200 bg-white px-3.5 py-2.5 text-sm text-gray-900 placeholder:text-gray-400 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 focus:outline-none transition';

const smallButtonClass =
  'inline-flex items-center justify-center px-2.5 py-1.5 text-xs font-semibold rounded-md border transition cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed';

let nextClientKey = 0;
function clientKey() {
  nextClientKey += 1;
  return `new-${nextClientKey}`;
}

function blankEmailStep() {
  return { key: clientKey(), id: null, step_type: 'send_email', subject: '', body: '', wait_amount: null, wait_unit: null };
}

function blankWaitStep() {
  return { key: clientKey(), id: null, step_type: 'wait', subject: '', body: '', wait_amount: 2, wait_unit: 'days' };
}

function normalizeSteps(steps) {
  return (steps || []).map((step) => ({
    key: step.id ? `step-${step.id}` : clientKey(),
    id: step.id ?? null,
    step_type: step.step_type,
    subject: step.subject ?? '',
    body: step.body ?? '',
    wait_amount: step.wait_amount ?? null,
    wait_unit: step.wait_unit ?? null,
  }));
}

function VariableChips({ variables }) {
  const [copied, setCopied] = useState(null);

  const copy = async (token) => {
    try {
      await navigator.clipboard.writeText(token);
      setCopied(token);
      setTimeout(() => setCopied(null), 1200);
    } catch {
      setCopied(null);
    }
  };

  return (
    <div className="rounded-lg bg-gray-50 border border-gray-200 p-3">
      <p className="text-xs font-semibold text-gray-700 mb-2">Variables (click to copy)</p>
      <div className="flex flex-wrap gap-1.5">
        {variables.map((name) => {
          const token = `{{${name}}}`;
          return (
            <button
              key={name}
              type="button"
              onClick={() => copy(token)}
              className="px-2 py-1 rounded-md bg-white border border-gray-200 text-xs font-mono text-gray-700 hover:border-indigo-300 hover:text-indigo-700 transition cursor-pointer"
            >
              {copied === token ? 'copied!' : token}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function EmailStepFields({ step, index, onChange, variables }) {
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);

  const runPreview = async () => {
    setPreviewing(true);
    try {
      const response = await fetch('/superadmin/email_journeys/preview', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken() },
        body: JSON.stringify({ subject: step.subject, body: step.body }),
      });
      setPreview(await response.json());
    } catch {
      setPreview({ error: 'Could not render a preview. Try again.' });
    } finally {
      setPreviewing(false);
    }
  };

  return (
    <div className="space-y-3">
      <div>
        <label htmlFor={`subject-${index}`} className="block text-sm font-semibold text-gray-700 mb-1.5">Subject</label>
        <input
          type="text"
          id={`subject-${index}`}
          value={step.subject}
          onChange={(e) => onChange({ ...step, subject: e.target.value })}
          placeholder="Welcome to your {{license_name}} training"
          className={inputClass}
        />
      </div>

      <div>
        <label htmlFor={`body-${index}`} className="block text-sm font-semibold text-gray-700 mb-1.5">Body</label>
        <textarea
          id={`body-${index}`}
          value={step.body}
          onChange={(e) => onChange({ ...step, body: e.target.value })}
          rows={7}
          placeholder={'Hi {{first_name}},\n\nYour training is ready at {{portal_url}}.'}
          className={`${inputClass} font-mono`}
        />
        <p className="mt-1 text-xs text-gray-500">
          Plain text. Line breaks are preserved. An unsubscribe footer is added automatically.
        </p>
      </div>

      <VariableChips variables={variables} />

      <div>
        <button
          type="button"
          onClick={runPreview}
          disabled={previewing}
          className={`${smallButtonClass} text-gray-700 bg-white border-gray-200 hover:bg-gray-50`}
        >
          {previewing ? 'Rendering…' : 'Preview with sample data'}
        </button>
      </div>

      {preview && (
        <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 space-y-2">
          {preview.error ? (
            <p className="text-xs text-red-700">{preview.error}</p>
          ) : (
            <>
              {preview.unknown_variables?.length > 0 && (
                <p className="text-xs text-red-700">
                  Unknown variables: {preview.unknown_variables.map((v) => `{{${v}}}`).join(', ')}
                </p>
              )}
              <p className="text-xs text-gray-500">Subject</p>
              <p className="text-sm font-semibold text-gray-900">{preview.subject}</p>
              <p className="text-xs text-gray-500">Body</p>
              <p className="text-sm text-gray-800 whitespace-pre-wrap">{preview.body}</p>
              <p className="text-xs text-gray-400 break-all border-t border-gray-200 pt-2">
                Unsubscribe link: {preview.unsubscribe_url}
              </p>
            </>
          )}
        </div>
      )}
    </div>
  );
}

function WaitStepFields({ step, index, onChange, waitUnits }) {
  return (
    <div className="flex flex-wrap items-end gap-3">
      <div className="w-32">
        <label htmlFor={`amount-${index}`} className="block text-sm font-semibold text-gray-700 mb-1.5">Wait</label>
        <input
          type="number"
          id={`amount-${index}`}
          min="1"
          value={step.wait_amount ?? ''}
          onChange={(e) => onChange({ ...step, wait_amount: e.target.value })}
          className={inputClass}
        />
      </div>
      <div className="w-40">
        <label htmlFor={`unit-${index}`} className="block text-sm font-semibold text-gray-700 mb-1.5">Unit</label>
        <select
          id={`unit-${index}`}
          value={step.wait_unit ?? ''}
          onChange={(e) => onChange({ ...step, wait_unit: e.target.value })}
          className={`${inputClass} cursor-pointer`}
        >
          {waitUnits.map((unit) => <option key={unit} value={unit}>{unit}</option>)}
        </select>
      </div>
      <p className="text-xs text-gray-500 pb-3">before the next step runs.</p>
    </div>
  );
}

export default function EmailJourneyForm({
  journey,
  mode,
  availableLicenses = [],
  supportedVariables = [],
  waitUnits = [],
  activeEnrollmentCount = 0,
  structuralEditsLocked = false,
  errors = [],
}) {
  const [name, setName] = useState(journey.name || '');
  const [description, setDescription] = useState(journey.description || '');
  const [licenseIds, setLicenseIds] = useState(journey.license_ids || []);
  const [enrollAssignee, setEnrollAssignee] = useState(journey.enroll_assignee ?? true);
  const [enrollPurchaser, setEnrollPurchaser] = useState(journey.enroll_purchaser ?? false);
  const [steps, setSteps] = useState(() => normalizeSteps(journey.steps));
  const [removedStepIds, setRemovedStepIds] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const isEdit = mode === 'edit';

  const toggleLicense = (id) => {
    setLicenseIds(licenseIds.includes(id) ? licenseIds.filter((x) => x !== id) : [...licenseIds, id]);
  };

  const updateStep = (index, next) => setSteps(steps.map((s, i) => (i === index ? next : s)));
  const addStep = (step) => setSteps([...steps, step]);

  // Dropping a saved step from the list is not enough: nested attributes leave
  // any record that is simply absent from the params untouched, so it would
  // stay in the database — and keep occupying its position. Remember the ids
  // so submit can send an explicit _destroy for each one.
  const removeStep = (index) => {
    const step = steps[index];
    if (step.id) setRemovedStepIds([...removedStepIds, step.id]);
    setSteps(steps.filter((_, i) => i !== index));
  };

  const moveStep = (index, delta) => {
    const target = index + delta;
    if (target < 0 || target >= steps.length) return;
    const next = [...steps];
    [next[index], next[target]] = [next[target], next[index]];
    setSteps(next);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setSubmitting(true);

    const payload = {
      email_journey: {
        name: name.trim(),
        description: description.trim(),
        enroll_assignee: enrollAssignee,
        enroll_purchaser: enrollPurchaser,
        // Always send at least one entry so clearing every checkbox actually
        // clears the triggers server-side.
        license_ids: licenseIds.length > 0 ? licenseIds : [''],
        // Position comes from array order, which is what the up/down controls
        // manipulate — the server never has to infer ordering.
        steps_attributes: [
          ...steps.map((step, index) => ({
            id: step.id,
            position: index,
            step_type: step.step_type,
            subject: step.step_type === 'send_email' ? step.subject : '',
            body: step.step_type === 'send_email' ? step.body : '',
            wait_amount: step.step_type === 'wait' ? step.wait_amount : null,
            wait_unit: step.step_type === 'wait' ? step.wait_unit : null,
          })),
          ...removedStepIds.map((id) => ({ id, _destroy: '1' })),
        ],
      },
    };

    const options = { onFinish: () => setSubmitting(false) };
    if (isEdit) {
      router.patch(`/superadmin/email_journeys/${journey.id}`, payload, options);
    } else {
      router.post('/superadmin/email_journeys', payload, options);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {errors.length > 0 && (
        <div className="rounded-xl border border-red-200 bg-red-50 p-4">
          <p className="text-sm font-semibold text-red-800 mb-1">This journey could not be saved</p>
          <ul className="list-disc list-inside text-sm text-red-700 space-y-0.5">
            {errors.map((message, i) => <li key={i}>{message}</li>)}
          </ul>
        </div>
      )}

      {structuralEditsLocked && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-4">
          <p className="text-sm text-amber-900">
            This journey has {activeEnrollmentCount} active {activeEnrollmentCount === 1 ? 'enrollment' : 'enrollments'}.
            Step structure is locked until those enrollments finish or are cancelled.
            Email content can still be edited.
          </p>
        </div>
      )}

      <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm space-y-5">
        <h2 className="text-lg font-semibold text-gray-900">Journey</h2>

        <div>
          <label htmlFor="name" className="block text-sm font-semibold text-gray-700 mb-1.5">Name</label>
          <input
            type="text"
            id="name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            autoFocus
            placeholder="e.g. RCFE Caregiver onboarding"
            className={inputClass}
          />
        </div>

        <div>
          <label htmlFor="description" className="block text-sm font-semibold text-gray-700 mb-1.5">
            Description <span className="font-normal text-gray-400">(optional)</span>
          </label>
          <input
            type="text"
            id="description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Internal note about what this sequence is for"
            className={inputClass}
          />
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm space-y-4">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Trigger licenses</h2>
          <p className="mt-1 text-sm text-gray-500">
            Enrollment starts when one of these licenses is purchased or assigned.
          </p>
        </div>

        {availableLicenses.length === 0 ? (
          <p className="text-sm text-gray-500">No licenses exist yet.</p>
        ) : (
          <div className="grid sm:grid-cols-2 gap-2 max-h-72 overflow-y-auto">
            {availableLicenses.map((license) => (
              <label
                key={license.id}
                className="flex items-start gap-2.5 rounded-lg border border-gray-200 px-3 py-2 hover:bg-gray-50 cursor-pointer"
              >
                <input
                  type="checkbox"
                  checked={licenseIds.includes(license.id)}
                  onChange={() => toggleLicense(license.id)}
                  className="mt-0.5 h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 cursor-pointer"
                />
                <span className="text-sm text-gray-800">{license.label}</span>
              </label>
            ))}
          </div>
        )}
      </div>

      <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm space-y-4">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Audience</h2>
          <p className="mt-1 text-sm text-gray-500">Who gets enrolled. These are independent — you can pick both.</p>
        </div>

        <label className="flex items-start gap-2.5 cursor-pointer">
          <input
            type="checkbox"
            checked={enrollAssignee}
            onChange={(e) => setEnrollAssignee(e.target.checked)}
            className="mt-0.5 h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 cursor-pointer"
          />
          <span className="text-sm">
            <span className="font-semibold text-gray-800">Enroll the assigned user</span>
            <span className="block text-gray-500">The person who receives the license and takes the training.</span>
          </span>
        </label>

        <label className="flex items-start gap-2.5 cursor-pointer">
          <input
            type="checkbox"
            checked={enrollPurchaser}
            onChange={(e) => setEnrollPurchaser(e.target.checked)}
            className="mt-0.5 h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 cursor-pointer"
          />
          <span className="text-sm">
            <span className="font-semibold text-gray-800">Enroll the purchaser</span>
            <span className="block text-gray-500">
              The person or facility that paid. For someone buying for themselves this is the same person,
              and they are only enrolled once.
            </span>
          </span>
        </label>
      </div>

      <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm space-y-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Steps</h2>
            <p className="mt-1 text-sm text-gray-500">Runs top to bottom when someone is enrolled.</p>
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => addStep(blankEmailStep())}
              disabled={structuralEditsLocked}
              className={`${smallButtonClass} text-white bg-indigo-600 border-indigo-600 hover:bg-indigo-700`}
            >
              + Send email
            </button>
            <button
              type="button"
              onClick={() => addStep(blankWaitStep())}
              disabled={structuralEditsLocked}
              className={`${smallButtonClass} text-gray-700 bg-white border-gray-200 hover:bg-gray-50`}
            >
              + Wait
            </button>
          </div>
        </div>

        {steps.length === 0 ? (
          <p className="text-sm text-gray-500 py-6 text-center">
            No steps yet. Add a Send email step to get started.
          </p>
        ) : (
          <div className="space-y-4">
            {steps.map((step, index) => (
              <div key={step.key} className="rounded-xl border border-gray-200 p-4 space-y-3">
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-2">
                    <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-gray-100 text-xs font-bold text-gray-600">
                      {index + 1}
                    </span>
                    <span className="text-sm font-semibold text-gray-900">
                      {step.step_type === 'send_email' ? 'Send email' : 'Wait'}
                    </span>
                  </div>
                  <div className="flex gap-1.5">
                    <button
                      type="button"
                      onClick={() => moveStep(index, -1)}
                      disabled={structuralEditsLocked || index === 0}
                      aria-label="Move step up"
                      className={`${smallButtonClass} text-gray-700 bg-white border-gray-200 hover:bg-gray-50`}
                    >
                      ↑
                    </button>
                    <button
                      type="button"
                      onClick={() => moveStep(index, 1)}
                      disabled={structuralEditsLocked || index === steps.length - 1}
                      aria-label="Move step down"
                      className={`${smallButtonClass} text-gray-700 bg-white border-gray-200 hover:bg-gray-50`}
                    >
                      ↓
                    </button>
                    <button
                      type="button"
                      onClick={() => removeStep(index)}
                      disabled={structuralEditsLocked}
                      className={`${smallButtonClass} text-red-700 bg-white border-red-200 hover:bg-red-50`}
                    >
                      Remove
                    </button>
                  </div>
                </div>

                {step.step_type === 'send_email' ? (
                  <EmailStepFields
                    step={step}
                    index={index}
                    onChange={(next) => updateStep(index, next)}
                    variables={supportedVariables}
                  />
                ) : (
                  <WaitStepFields
                    step={step}
                    index={index}
                    onChange={(next) => updateStep(index, next)}
                    waitUnits={waitUnits}
                  />
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="flex flex-col-reverse sm:flex-row gap-3">
        <Link
          href="/superadmin/email_journeys"
          className="flex-1 inline-flex items-center justify-center rounded-lg border border-gray-200 bg-white px-4 py-2.5 text-sm font-semibold text-gray-700 hover:bg-gray-50 transition"
        >
          Cancel
        </Link>
        <button
          type="submit"
          disabled={submitting}
          className="flex-1 inline-flex items-center justify-center rounded-lg bg-indigo-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition cursor-pointer"
        >
          {submitting ? 'Saving…' : isEdit ? 'Save changes' : 'Create journey'}
        </button>
      </div>
    </form>
  );
}
